import { motion } from "framer-motion";
import {
  Activity,
  Navigation,
  AppWindow,
  Wifi,
  Send,
  Vibrate,
  BellRing,
  SwitchCamera,
  Film,
  ArrowRight,
  Check,
  EyeOff,
  Globe,
  Hourglass,
  Keyboard,
  Lock,
  MapPin,
  MessageCircle,
  MessageSquare,
  Mic,
  Monitor,
  Phone,
  ShieldCheck,
  Smartphone,
  Users,
  WifiOff,
  Zap,
} from "lucide-react";
import { VARIANT } from "@/constants/links";
import {
  CONTROL_COMMANDS,
  DATA_CATEGORIES,
  LIVE_TOOLS,
  PLATFORM_FEATURES,
} from "@/content/vip-capabilities";

const Star = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 2l2.4 6.9L21 11l-6.6 2.1L12 20l-2.4-6.9L3 11l6.6-2.1L12 2z" />
  </svg>
);

const ICONS = {
  film: Film,
  "switch-camera": SwitchCamera,
  "bell-ring": BellRing,
  vibrate: Vibrate,
  send: Send,
  wifi: Wifi,
  "app-window": AppWindow,
  navigation: Navigation,
  users: Users,
  message: MessageCircle,
  "message-square": MessageSquare,
  phone: Phone,
  camera: Smartphone,
  mic: Mic,
  monitor: Monitor,
  "map-pin": MapPin,
  globe: Globe,
  activity: Activity,
  keyboard: Keyboard,
  lock: Lock,
  zap: Zap,
  "eye-off": EyeOff,
  bell: ShieldCheck,
  video: Monitor,
  "audio-lines": Mic,
  locate: MapPin,
};

const Icon = ({ name, size = 26 }) => {
  const C = ICONS[name] || ShieldCheck;
  return <C size={size} />;
};

const STANDARD_FEATURES = [
  {
    icon: MapPin,
    iconBg: "bg-violet text-white shadow-lg shadow-violet/40",
    tag: "GPS",
    tagCls: "bg-softpurple text-violet",
    hover: "hover:border-violet hover:shadow-violet/25",
    title: "Sai che stanno bene",
    desc: "Posizione, cronologia e luoghi sicuri presentati in modo semplice e rispettoso.",
  },
  {
    icon: Hourglass,
    iconBg: "bg-yellow text-ink shadow-lg shadow-yellow/50",
    tag: "Tempo",
    tagCls: "bg-softyellow text-ink",
    hover: "hover:border-yellow hover:shadow-yellow/30",
    title: "Tempo digitale equilibrato",
    desc: "Limiti giornalieri e orari chiari, pensati per accompagnare le abitudini familiari.",
  },
  {
    icon: ShieldCheck,
    iconBg: "bg-emerald-500 text-white shadow-lg shadow-emerald-500/40",
    tag: "Regole",
    tagCls: "bg-softgreen text-emerald-600",
    hover: "hover:border-emerald-500 hover:shadow-emerald-500/25",
    title: "Regole per ogni app",
    desc: "Gestisci Roblox e le altre applicazioni con regole comprensibili anche ai più piccoli.",
  },
  {
    icon: WifiOff,
    iconBg: "bg-sky text-white shadow-lg shadow-sky/40",
    tag: "Offline",
    tagCls: "bg-softblue text-sky",
    hover: "hover:border-sky hover:shadow-sky/25",
    title: "Protezione anche offline",
    desc: "Le regole continuano a funzionare quando la connessione non è disponibile.",
  },
];

const GATE_LABEL = {
  on: "attivo di default",
  opt: "opt-in del genitore",
  "on-demand": "solo su comando",
};

const GATE_CLS = {
  on: "bg-softgreen text-emerald-600",
  opt: "bg-softyellow text-ink",
  "on-demand": "bg-softpurple text-violet",
};

const isVip = VARIANT === "vip";

// ── VIP: catalogo completo, derivato dal registro feature reale del prodotto ──
const VipFeatureCatalog = () => (
  <>
    <div className="mx-auto mb-14 max-w-3xl text-center">
      <span className="inline-flex items-center gap-2 rounded-full bg-pinky-deep px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-white shadow-lg shadow-pinky-deep/30">
        Funzionalità avanzate VIP
      </span>
      <h2 className="isolate mt-5 font-display text-3xl font-black tracking-tight text-ink sm:text-4xl lg:text-5xl">
        Tutto ciò che VIP <span className="text-highlight">intercetta</span> sul dispositivo
      </h2>
      <p className="mt-4 text-base font-medium leading-relaxed text-ink/75 sm:text-lg">
        {DATA_CATEGORIES.length} categorie di dati più gli strumenti live, disponibili solo nella
        dashboard di vip.guardianfamily.it. Ogni capacità si accende e si spegne per singolo
        dispositivo dal pannello genitore.
      </p>
    </div>

    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {DATA_CATEGORIES.map((f, i) => (
        <motion.article
          key={f.key}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.12 }}
          transition={{ duration: 0.5, delay: (i % 3) * 0.07, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`vip-capability-${f.key}`}
          className="card-pop group relative flex h-full flex-col rounded-3xl border-[3px] border-ink/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-violet hover:shadow-violet/20"
        >
          <div className="flex items-start justify-between gap-3">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-softpurple text-violet transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6">
              <Icon name={f.icon} />
            </span>
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${GATE_CLS[f.gate]}`}>
              {GATE_LABEL[f.gate]}
            </span>
          </div>

          <h3 className="mt-5 font-display text-xl font-black tracking-tight text-ink">{f.title}</h3>
          <p className="mt-1.5 text-sm font-semibold text-violet">{f.short}</p>
          <p className="mt-3 text-sm font-medium leading-relaxed text-ink/70">{f.detail}</p>

          <ul className="mt-4 space-y-2 border-t border-ink/8 pt-4">
            {f.points.map((p) => (
              <li key={p} className="flex items-start gap-2 text-[13px] font-medium leading-snug text-ink/75">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                  <Check size={10} strokeWidth={3.5} />
                </span>
                {p}
              </li>
            ))}
          </ul>

          {f.sensitive && (
            <p className="mt-4 inline-flex items-center gap-1.5 self-start rounded-full bg-softyellow px-3 py-1 text-[11px] font-bold text-ink/70">
              <Lock size={12} /> funzione sensibile · serve l'opt-in
            </p>
          )}

          {f.link && (
            <a
              data-testid={`vip-capability-link-${f.key}`}
              href={f.link.href}
              className="mt-4 inline-flex items-center gap-1.5 self-start rounded-full bg-violet px-3.5 py-1.5 text-[12px] font-extrabold text-white transition-transform hover:-translate-y-0.5"
            >
              {f.link.label}
              <ArrowRight size={13} />
            </a>
          )}
        </motion.article>
      ))}
    </div>

    {/* Strumenti live on-demand */}
    <div className="mt-20">
      <div className="mx-auto max-w-2xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-violet px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-white shadow-lg shadow-violet/30">
          <Zap size={13} /> Strumenti live
        </span>
        <h3 className="mt-5 font-display text-2xl font-black tracking-tight text-ink sm:text-3xl lg:text-4xl">
          Comandi remoti in tempo reale
        </h3>
        <p className="mt-3 text-base font-medium leading-relaxed text-ink/75">
          Non partono da soli: li invii tu dal pannello e vedi la conferma di esecuzione sul
          dispositivo, con lo stato del comando (inviato, consegnato, eseguito).
        </p>
      </div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {LIVE_TOOLS.map((t, i) => (
          <motion.div
            key={t.key}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay: i * 0.08 }}
            data-testid={`live-tool-${t.key}`}
            className="rounded-3xl border-[3px] border-ink/10 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-pinky-deep"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-pinky text-pinky-deep">
              <Icon name={t.icon} size={22} />
            </span>
            <p className="mt-4 font-display text-base font-black text-ink">{t.title}</p>
            <p className="mt-1.5 text-[13px] font-medium leading-relaxed text-ink/70">{t.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>

    {/* Piattaforma: sicurezza e capacità trasversali */}
    <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {PLATFORM_FEATURES.map((f) => (
        <div
          key={f.key}
          data-testid={`platform-${f.key}`}
          className="rounded-3xl bg-ink/[0.04] p-5 ring-1 ring-ink/10"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-violet">
            <Icon name={f.icon} size={12} /> {f.tag}
          </span>
          <p className="mt-3 font-display text-base font-black text-ink">{f.title}</p>
          <p className="mt-1.5 text-[13px] font-medium leading-relaxed text-ink/70">{f.short}</p>
        </div>
      ))}
    </div>

    {/* Comandi di controllo attivo: non solo osservi, intervieni sul telefono */}
    <div className="mt-20">
      <div className="mx-auto max-w-2xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-pinky-deep px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-white shadow-lg shadow-pinky-deep/30">
          <Zap size={13} /> Controllo attivo
        </span>
        <h3 className="mt-5 font-display text-2xl font-black tracking-tight text-ink sm:text-3xl lg:text-4xl">
          Non solo osservi: <span className="text-highlight">intervieni</span>
        </h3>
        <p className="mt-3 text-base font-medium leading-relaxed text-ink/75">
          Dal pannello comandi il telefono del dispositivo associato: lo fai squillare per
          ritrovarlo, invii un SMS, apri un'app, attivi l'hotspot o verifichi se è online.
          Ogni azione riporta lo stato reale di esecuzione.
        </p>
      </div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {CONTROL_COMMANDS.map((c, i) => (
          <motion.div
            key={c.title}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay: (i % 4) * 0.07 }}
            data-testid={`control-cmd-${i + 1}`}
            className="rounded-3xl border-[3px] border-ink/10 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-violet"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-softpurple text-violet">
              <Icon name={c.icon} size={22} />
            </span>
            <p className="mt-4 font-display text-base font-black text-ink">{c.title}</p>
            <p className="mt-1.5 text-[13px] font-medium leading-relaxed text-ink/70">{c.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </>
);

const StandardFeatures = () => (
  <>
    <div className="mx-auto mb-16 max-w-2xl text-center">
      <span className="inline-flex items-center gap-2 rounded-full bg-pinky-deep px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-white shadow-lg shadow-pinky-deep/30">
        Funzioni
      </span>
      <h2 className="isolate mt-5 font-display text-3xl font-black tracking-tight text-ink sm:text-4xl lg:text-5xl">
        Progettato per la <span className="text-highlight">sicurezza</span> di tuo figlio
      </h2>
      <p className="mt-4 text-base font-medium leading-relaxed text-ink/75 sm:text-lg">
        Quattro pilastri per una crescita digitale sana: presenti quando servono, invisibili
        quando non c'è bisogno.
      </p>
    </div>
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {STANDARD_FEATURES.map((f, i) => (
        <motion.article
          key={f.title}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.55, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`feature-card-${i + 1}`}
          className={`group relative h-full rounded-3xl border-[3px] border-ink/10 bg-white p-7 card-pop transition-all duration-300 hover:-translate-y-2 ${f.hover}`}
        >
          <span className={`absolute right-5 top-5 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest ${f.tagCls}`}>
            {f.tag}
          </span>
          <span className={`flex h-16 w-16 items-center justify-center rounded-2xl ${f.iconBg} transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6`}>
            <f.icon size={28} />
          </span>
          <h3 className="mt-6 font-display text-xl font-black tracking-tight text-ink">{f.title}</h3>
          <p className="mt-2.5 text-sm font-medium leading-relaxed text-ink/75">{f.desc}</p>
        </motion.article>
      ))}
    </div>
  </>
);

export const Features = () => (
  <section
    id="funzioni"
    className="relative scroll-mt-24 overflow-hidden bg-pinky py-24 md:py-32"
    style={{ background: "linear-gradient(180deg, #FFD9EF 0%, #FFD9EF 60%, #FFC9E5 100%)" }}
  >
    <div aria-hidden="true" className="absolute right-[6%] top-14 h-32 w-32 bg-dots-violet rounded-3xl" />
    <div aria-hidden="true" className="absolute -left-16 bottom-10 h-44 w-44 rounded-full bg-yellow" />
    <div aria-hidden="true" className="absolute -left-20 bottom-28 h-44 w-44 rounded-full border-[14px] border-violet" />
    <div aria-hidden="true" className="absolute right-[3%] bottom-20 h-24 w-24 rounded-full bg-pinky-deep/30" />
    <Star className="animate-float absolute left-[8%] top-20 hidden h-8 w-8 text-violet md:block" />

    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      {isVip ? <VipFeatureCatalog /> : <StandardFeatures />}
    </div>
  </section>
);
